/**
 * The scoreboard.
 *
 * Your own runs, kept in localStorage. There is deliberately no invented
 * competition here — a board of fake strangers is a lie the player finds out
 * about, and "you got to floor 6 last time" is a sharper hook anyway. The
 * shape below is what a server would return, so a real board can be dropped in
 * later without touching the screen that draws it.
 */

export interface ScoreRow {
  /** Milliseconds since the epoch. */
  at: number;
  score: number;
  depth: number;
  kills: number;
  /** Seconds survived. */
  time: number;
  seed: string;
  /** Set on the row from the run that just ended. */
  fresh?: boolean;
}

const KEY = 'levelspec.scores.v1';
const MAX = 20;

export function loadScores(): ScoreRow[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const rows = JSON.parse(raw) as ScoreRow[];
    return Array.isArray(rows) ? rows.filter((r) => typeof r?.score === 'number') : [];
  } catch {
    return [];
  }
}

export function saveScore(row: ScoreRow): ScoreRow[] {
  const rows = loadScores();
  rows.push(row);
  rows.sort((a, b) => b.score - a.score);
  const kept = rows.slice(0, MAX);
  try {
    localStorage.setItem(KEY, JSON.stringify(kept.map(({ fresh, ...r }) => r)));
  } catch {
    /* private browsing; the board is just not persistent then */
  }
  return kept;
}

/** Where this run landed, 1-based, or 0 if it did not make the board. */
export function rankOf(rows: ScoreRow[], row: ScoreRow): number {
  const i = rows.findIndex((r) => r.at === row.at && r.score === row.score);
  return i < 0 ? 0 : i + 1;
}

export function bestDepth(rows: ScoreRow[]): number {
  return rows.reduce((a, r) => Math.max(a, r.depth), 0);
}
